import { Suspense, useCallback, useEffect } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import * as THREE from "three";

import { Bubbles } from "@/components/engine/Bubbles";
import { CausticLight } from "@/components/engine/CausticLight";
import { DAY_BG, EnvironmentSetup, NIGHT_BG } from "@/components/engine/EnvironmentSetup";
import { IsometricCamera } from "@/components/engine/IsometricCamera";
import { LobsterAgent } from "@/components/engine/LobsterAgent";
import { NightMotes } from "@/components/engine/NightMotes";

import { OfficeCalendar } from "@/components/engine/OfficeCalendar";
import { OfficeComputer } from "@/components/engine/OfficeComputer";
import { OfficeFileCabinet } from "@/components/engine/OfficeFileCabinet";
import { OfficeMailbox } from "@/components/engine/OfficeMailbox";
import { OfficeNotebook } from "@/components/engine/OfficeNotebook";
import { OfficeTelephone } from "@/components/engine/OfficeTelephone";
import { Pebbles } from "@/components/engine/Pebbles";
import { ProceduralCorals } from "@/components/engine/ProceduralCorals";
import { SandPlatform } from "@/components/engine/SandPlatform";
import { useNavMesh } from "@/hooks/useNavMesh";
import { useRTSControls } from "@/hooks/useRTSControls";
import { useEngineStore } from "@/store/useEngineStore";
import { useTheme } from "@/hooks/useTheme";

/** Applies theme-dependent tone-mapping exposure without recreating the renderer. */
function ExposureController({ isNight }: { isNight: boolean }) {
  const gl = useThree((state) => state.gl);
  useEffect(() => {
    gl.toneMappingExposure = isNight ? 1.05 : 1.2;
  }, [gl, isNight]);
  return null;
}

export function CommandViewport() {
  // Theme is read outside the Canvas (R3F v8 does not bridge React context)
  // and passed down as props. Light mode values are byte-identical to the
  // original bright-noon scene.
  const { isDark } = useTheme();

  return (
    <div
      className="h-full w-full select-none touch-none"
      style={{
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
        backgroundColor: isDark ? NIGHT_BG : DAY_BG,
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Canvas
        shadows="soft"
        dpr={[1, 1.75]}
        camera={{ position: [12, 12, 12], fov: 50, near: 0.1, far: 120 }}
        gl={{ toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: 1.2 }}
      >
        <Suspense fallback={null}>
          <ExposureController isNight={isDark} />
          <IsometricCamera />
          <EnvironmentSetup isNight={isDark} />
          <SceneContent isNight={isDark} />
        </Suspense>
      </Canvas>
    </div>
  );
}

function SceneContent({ isNight }: { isNight: boolean }) {
  const { computePath } = useNavMesh();

  // Walk agent toward a prop when clicked — stop 1.5 units away
  const walkToProp = useCallback(
    (e: Event) => {
      const { position } = (e as CustomEvent).detail as { position: [number, number, number]; propId: string };
      const store = useEngineStore.getState();
      const agent = store.agents["claw-1"];
      if (!agent) return;

      // Always deselect before walking
      if (store.selectedAgentId) {
        store.selectAgent({ id: null });
      }

      // Walk toward the prop and stop 1.5 units away
      const dx = agent.position[0] - position[0];
      const dz = agent.position[2] - position[2];
      const dist = Math.sqrt(dx * dx + dz * dz);
      const offset = dist > 0 ? 1.5 / dist : 1;
      const target: [number, number, number] = [
        position[0] + dx * offset,
        0,
        position[2] + dz * offset,
      ];

      const path = computePath(agent.position, target);
      store.setMoveTarget({ agentId: "claw-1", target, path });
    },
    [computePath],
  );

  useEffect(() => {
    window.addEventListener("prop-walk", walkToProp);
    return () => window.removeEventListener("prop-walk", walkToProp);
  }, [walkToProp]);

  // Idle wandering: every 5s, pick a random point on the diorama and walk there
  useEffect(() => {
    const RADIUS = 6;
    const interval = setInterval(() => {
      const store = useEngineStore.getState();
      const agent = store.agents["claw-1"];
      if (!agent || agent.status === "moving") return;

      const angle = Math.random() * Math.PI * 2;
      const dist = Math.random() * RADIUS;
      const target: [number, number, number] = [
        Math.cos(angle) * dist,
        0,
        Math.sin(angle) * dist,
      ];

      const path = computePath(agent.position, target);
      store.setMoveTarget({ agentId: "claw-1", target, path });
    }, 5000);
    return () => clearInterval(interval);
  }, [computePath]);

  const { handleClick, handlePointerDown } = useRTSControls();

  return (
    <>
      <SandPlatform
        onClick={handleClick}
        onPointerDown={handlePointerDown}
      />
      {/* CausticLight memoizes its uniforms once — re-key to apply theme values */}
      {isNight ? (
        <CausticLight key="night" size={15} intensity={0.1} scale={4.0} color="#4FD8E8" />
      ) : (
        <CausticLight key="day" size={15} intensity={0.2} scale={4.0} color="#B0C4DE" />
      )}
      <ProceduralCorals isNight={isNight} />
      <OfficeComputer isNight={isNight} />
      <OfficeTelephone />
      <OfficeFileCabinet />
      <OfficeCalendar />
      <OfficeMailbox />
      <OfficeNotebook />
      <Bubbles />
      {isNight && <NightMotes />}
      <Pebbles />
      <LobsterAgent onPointerDown={handlePointerDown} />
    </>
  );
}
